import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Megaphone, Loader2, Check, AlertTriangle, Send } from "lucide-react";
import getApiUrl from "@/utils/api";

const API = getApiUrl();

const DEFAULT_MESSAGE = `*Easy Transportation*

Dear Easy Transportation Family,

Due to the rising gas prices from the ongoing conflict in the Middle East, we're forced to add the following surcharges to every ride:

• Chicago / Milwaukee: +$20
• Midway: +$35
• Madison: +$10

Thank you for your understanding. Same drivers, same on-time service, same care.

— Easy Transportation Team
Easy Ride, Exceptional Experience`;

/**
 * Surcharge Announcement Blast button for MessagesTab.
 * Dry-run preview → confirm → actual send via Twilio WhatsApp.
 */
export default function SurchargeBlastButton({ getAuthHeaders }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const runDryRun = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/whatsapp/surcharge-blast`,
        { dry_run: true, custom_message: message },
        getAuthHeaders()
      );
      setPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    }
    setLoading(false);
  };

  const sendBlast = async () => {
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/admin/whatsapp/surcharge-blast`,
        { dry_run: false, custom_message: message },
        getAuthHeaders()
      );
      setResult(res.data);
      if (res.data.sent > 0) {
        toast.success(`Sent to ${res.data.sent} customer${res.data.sent !== 1 ? "s" : ""}!`);
      }
      if (res.data.failed > 0) {
        toast.warning(`${res.data.failed} message${res.data.failed !== 1 ? "s" : ""} failed — see details in dialog`, { duration: 7000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
    setSending(false);
  };

  const reset = () => {
    setPreview(null);
    setResult(null);
    setMessage(DEFAULT_MESSAGE);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button
          className="bg-amber-600 hover:bg-amber-700 text-white rounded-full h-9 px-4 text-sm font-semibold whitespace-nowrap"
          data-testid="surcharge-blast-btn"
        >
          <Megaphone className="w-4 h-4 mr-1.5" />
          Send Surcharge Announcement
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="surcharge-blast-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-amber-600" />
            Surcharge Announcement Blast
          </DialogTitle>
          <DialogDescription>
            Send the fuel-surcharge notice to every active future booking via WhatsApp.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <>
            {/* Message editor */}
            <div className="space-y-2">
              <label className="text-sm font-semibold">Message</label>
              <Textarea
                value={message}
                onChange={(e) => { setMessage(e.target.value); setPreview(null); }}
                className="min-h-[240px] text-xs font-mono rounded-xl"
                data-testid="surcharge-message-textarea"
              />
              <p className="text-[11px] text-muted-foreground">
                WhatsApp formatting: *bold*, _italic_, ~strikethrough~. Edits here won't be saved — one-time send.
              </p>
            </div>

            {/* Preview panel */}
            {preview && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-2" data-testid="surcharge-preview">
                <div className="flex items-center gap-2 text-sm font-semibold text-blue-900">
                  <Check className="w-4 h-4" /> Preview Ready
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
                  <div className="bg-white rounded-lg p-2">
                    <p className="text-[10px] text-muted-foreground uppercase">Eligible</p>
                    <p className="text-xl font-bold text-blue-700">{preview.eligible_recipients}</p>
                  </div>
                  <div className="bg-white rounded-lg p-2">
                    <p className="text-[10px] text-muted-foreground uppercase">Will Send</p>
                    <p className="text-xl font-bold text-green-700">{preview.will_send_now}</p>
                  </div>
                  <div className="bg-white rounded-lg p-2">
                    <p className="text-[10px] text-muted-foreground uppercase">Quota Left</p>
                    <p className="text-xl font-bold text-amber-700">{preview.daily_quota_remaining}</p>
                  </div>
                  <div className="bg-white rounded-lg p-2">
                    <p className="text-[10px] text-muted-foreground uppercase">Pending</p>
                    <p className="text-xl font-bold text-gray-700">{preview.will_remain_pending}</p>
                  </div>
                </div>
                {preview.will_remain_pending > 0 && (
                  <p className="text-xs text-amber-800 flex items-start gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    {preview.will_remain_pending} recipients won't fit in today's Twilio quota. Run this again tomorrow to reach them.
                  </p>
                )}
                {preview.sample_recipients?.length > 0 && (
                  <div className="text-xs">
                    <p className="text-muted-foreground mb-1">Sample recipients:</p>
                    <div className="flex flex-wrap gap-1">
                      {preview.sample_recipients.map((r, i) => (
                        <Badge key={i} variant="outline" className="text-[10px]">
                          {r.name} ({r.travel_date})
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Result panel */}
        {result && (
          <div className="space-y-3" data-testid="surcharge-result">
            <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4">
              <div className="flex items-center gap-2 text-lg font-bold text-green-900 mb-2">
                <Check className="w-5 h-5" /> Blast Complete
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-[10px] uppercase text-muted-foreground">Sent</p>
                  <p className="text-2xl font-bold text-green-700">{result.sent}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-muted-foreground">Failed</p>
                  <p className="text-2xl font-bold text-red-600">{result.failed}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-muted-foreground">Quota Left</p>
                  <p className="text-2xl font-bold text-amber-600">{result.daily_quota_remaining}</p>
                </div>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs">
                <p className="font-semibold text-red-900 mb-1">Failed deliveries:</p>
                <ul className="space-y-1">
                  {result.errors.map((e, i) => (
                    <li key={i} className="text-red-800">
                      …{e.phone} — {e.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {!result && !preview && (
            <Button
              onClick={runDryRun}
              disabled={loading || !message.trim()}
              variant="outline"
              className="w-full sm:w-auto"
              data-testid="surcharge-preview-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Preview Recipients
            </Button>
          )}
          {!result && preview && (
            <>
              <Button variant="outline" onClick={() => setPreview(null)} className="w-full sm:w-auto">
                Edit Message
              </Button>
              <Button
                onClick={sendBlast}
                disabled={sending || preview.will_send_now === 0}
                className="bg-amber-600 hover:bg-amber-700 text-white w-full sm:w-auto"
                data-testid="surcharge-confirm-send-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send to {preview.will_send_now} Recipient{preview.will_send_now !== 1 ? "s" : ""}
              </Button>
            </>
          )}
          {result && (
            <Button onClick={() => setOpen(false)} className="w-full sm:w-auto">Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
